import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import { faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { ExternalLink } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { useBannerDismissal } from "../../hooks/useBannerDismissal";
import { getPathfinderUrl } from "../../lib/pathfinder";

export const PATHFINDER_BANNER_NAME = "pathfinder_banner";

export default function PathfinderAzEsaBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\PathfinderAzEsaBanner",
  );
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const hasTrackedViewRef = React.useRef<boolean>(false);
  const { dismissBanner, isBannerDismissed } = useBannerDismissal();

  React.useEffect(() => {
    if (hasTrackedViewRef.current) {
      return;
    }

    hasTrackedViewRef.current = true;
    trackEvent("pathfinder_banner_view", {
      is_mobile: isMobile,
    });
  }, [isMobile, trackEvent]);

  const handleDismiss = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    trackEvent("pathfinder_banner_dismiss", {
      is_mobile: isMobile,
    });
    dismissBanner(PATHFINDER_BANNER_NAME);
  };

  if (isBannerDismissed(PATHFINDER_BANNER_NAME)) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary[600],
        minHeight: "50px",
        padding: "0 1em",
        [theme.breakpoints.down("md")]: {
          padding: "20px",
        },
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        <ExternalLink
          url={getPathfinderUrl("banner")}
          onClick={() => {
            trackEvent("pathfinder_banner_click", {
              is_mobile: isMobile,
            });
          }}
          sx={{
            display: "block",
            flex: 1,
            "&, &:visited, &:hover, &:focus, &:focus-visible, &:active": {
              color: "inherit !important",
              textDecoration: "none !important",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50px",
              flex: 1,
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              {t(
                "AZ ESA families: Use your funds without paying out of pocket. Try Pathfinder",
              )}
            </Typography>
          </Box>
        </ExternalLink>
        <Box
          component="button"
          type="button"
          aria-label={t("Close banner")}
          onClick={handleDismiss}
          sx={{
            alignItems: "center",
            backgroundColor: "transparent",
            border: 0,
            color: "common.white",
            display: "flex",
            justifyContent: "center",
            right: 0,
            width: "2rem",
            height: "2rem",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <Icon icon={faTimes} />
        </Box>
      </Box>
    </Box>
  );
}
